import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'
import {
  getAllBusStops,
  getAllBusRoutes,
  formatBusRoutes,
  hasCityBusData,
  BusStop,
  BusRoute,
} from '../services/busService'
import { formatDistance, calculateDistance } from '../services/nearbyPlaces'
import { Station } from '../types/metro.types'

interface NearbyStation {
  station: Station;
  distanceMeters: number;
}

function NearbyBus() {
  const { selectedCity, currentCity, stations, isLoading } = useMetro()
  const [selectedStopId, setSelectedStopId] = useState('')

  // Get all bus stops for the city
  const busStops = useMemo((): BusStop[] => {
    if (!selectedCity || !hasCityBusData(selectedCity)) {
      return []
    }
    return getAllBusStops(selectedCity)
  }, [selectedCity])

  // Get bus routes
  const busRoutes = useMemo((): BusRoute[] => {
    if (!selectedCity || !hasCityBusData(selectedCity)) {
      return []
    }
    return getAllBusRoutes(selectedCity)
  }, [selectedCity])

  // Get selected bus stop
  const selectedStop = useMemo(() => {
    return busStops.find(s => s.id === selectedStopId)
  }, [busStops, selectedStopId])

  // Get stations with coordinates
  const stationsWithCoords = useMemo(() => {
    return stations.filter(s => s.coordinates?.latitude && s.coordinates?.longitude)
  }, [stations])

  // Get nearby stations for selected bus stop
  const nearbyStations = useMemo((): NearbyStation[] => {
    if (!selectedStop || stationsWithCoords.length === 0) {
      return []
    }

    return stationsWithCoords
      .map(station => ({
        station,
        distanceMeters: calculateDistance(
          selectedStop.latitude,
          selectedStop.longitude,
          station.coordinates!.latitude,
          station.coordinates!.longitude
        ),
      }))
      .sort((a, b) => a.distanceMeters - b.distanceMeters)
      .slice(0, 5)
  }, [selectedStop, stationsWithCoords])

  // Check if city has bus data
  const hasData = selectedCity && hasCityBusData(selectedCity)

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">Select a City First</h2>
            <p className="text-neutral-dark/70 mb-4">
              Please select a city to explore bus connectivity near metro stations
            </p>
            <Link to="/cities" className="btn-primary inline-block">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-green border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-neutral-dark/70">Loading city data...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive">
        <h1 className="text-3xl lg:text-4xl 2xl:text-tv-2xl font-bold text-center mb-4">
          Nearby Bus Stops
        </h1>
        <p className="text-center text-neutral-dark/70 mb-8">
          Find bus connectivity near metro stations in {currentCity?.name}
        </p>

        {!hasData && (
          <div className="card text-center py-8 mb-8 bg-metro-yellow-soft">
            <span className="text-4xl mb-2 block">⚠️</span>
            <p className="text-neutral-dark/70">
              Bus connectivity data is not yet available for {currentCity?.name}.
              <br />Data available for: Delhi, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, Kochi, Lucknow, Jaipur, Nagpur, Ahmedabad
            </p>
          </div>
        )}

        {hasData && (
          <>
            {/* Bus Stop Selector */}
            <div className="card mb-8 max-w-xl mx-auto">
              <label className="block text-sm font-medium mb-2">Select Bus Stop</label>
              <select
                value={selectedStopId}
                onChange={(e) => setSelectedStopId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-green focus:border-transparent"
              >
                <option value="">Choose a bus stop to find nearest metro stations...</option>
                {busStops.map((stop) => (
                  <option key={stop.id} value={stop.id}>
                    {stop.name} {stop.type === 'bus_terminal' ? '(Terminal)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Nearby Stations */}
            {selectedStop ? (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-center mb-4">
                  Nearest Metro Stations to {selectedStop.name}
                </h2>
                {nearbyStations.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4 max-w-5xl mx-auto">
                    {nearbyStations.map(({ station, distanceMeters }, index) => (
                      <div key={station.id} className={`card ${index === 0 ? 'ring-2 ring-metro-blue bg-metro-blue-soft/30' : ''}`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                            index === 0 ? 'bg-metro-blue' : 'bg-gray-400'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{station.name}</h3>
                          </div>
                        </div>
                        <p className="text-xs text-neutral-dark/60 mb-2">
                          {station.lines.join(', ')}
                        </p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-metro-green font-medium">
                            📍 {formatDistance(distanceMeters)}
                          </span>
                          <span className="text-neutral-dark/50">
                            ~{Math.ceil(distanceMeters / 80)} min walk
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="card text-center py-8 text-neutral-dark/50 max-w-xl mx-auto">
                    <p>No stations with location data available</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="card text-center py-12 text-neutral-dark/50 mb-8">
                <span className="text-6xl mb-4 block">🚇</span>
                <p className="text-lg">Select a bus stop to see nearby metro stations</p>
              </div>
            )}

            {/* All Bus Stops List */}
            <h2 className="text-xl font-semibold mb-4">All Bus Stops in {currentCity?.name}</h2>
            <div className="space-y-4 max-w-3xl mx-auto mb-12">
              {busStops.map((stop) => (
                <div key={stop.id} className="card flex items-center gap-4">
                  <div className={`w-12 h-12 ${
                    stop.type === 'bus_terminal' ? 'bg-metro-green' : 'bg-metro-green-soft'
                  } rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <span className="text-2xl">{stop.type === 'bus_terminal' ? '🚏' : '🚌'}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{stop.name}</h3>
                      {stop.type === 'bus_terminal' && (
                        <span className="text-xs bg-metro-green text-white px-2 py-0.5 rounded-full">
                          Terminal
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-dark/70">
                      Routes: {formatBusRoutes(stop.routes)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedStopId(stop.id)}
                    className="text-sm text-metro-green hover:underline flex-shrink-0"
                  >
                    Find stations
                  </button>
                </div>
              ))}
            </div>

            {/* Bus Routes */}
            {busRoutes.length > 0 && (
              <>
                <h2 className="text-xl font-semibold mb-4">Major Bus Routes in {currentCity?.name}</h2>
                <div className="space-y-4 max-w-3xl mx-auto">
                  {busRoutes.map((route) => (
                    <div key={route.id} className="card">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-metro-green rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-lg">{route.number}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{route.name}</h3>
                          <div className="flex items-center gap-2 text-sm text-neutral-dark/70 mt-1">
                            <span className="bg-metro-green-soft px-2 py-0.5 rounded">{route.from}</span>
                            <span>→</span>
                            <span className="bg-metro-green-soft px-2 py-0.5 rounded">{route.to}</span>
                          </div>
                          <p className="text-sm text-metro-green mt-2">
                            ⏱️ {route.frequency}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default NearbyBus
